import { useRef, type RefObject } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Button } from "../../button";
import { Input } from "../../input";
import { cn } from "../../utils";
import { useDigitSegment } from "../useDigitSegment";
import type { DateTimePickerClassNames } from "../types";
import type { DateTimePickerPanelProps } from "./panelTypes";

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

const ARROW_CLASS =
  "h-6 w-9 rounded p-0 text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-1 focus-visible:ring-indigo-400";

const SEGMENT_CLASS =
  "h-auto w-9 cursor-default rounded border-0 bg-transparent p-0 text-center text-sm tabular-nums text-foreground shadow-none focus:bg-accent focus-visible:border-0 focus-visible:ring-0 selection:bg-primary selection:text-primary-foreground md:text-sm";

const wrap = (value: number, size: number) => ((value % size) + size) % size;

interface StepperSegmentProps {
  label: string;

  text: string;
  onStep: (delta: number) => void;
  inputRef: RefObject<HTMLInputElement>;
  handlers: Pick<
    ReturnType<typeof useDigitSegment>,
    "handleKeyDown" | "handleFocus" | "handleBlur"
  >;
}

function StepperSegment({ label, text, onStep, inputRef, handlers }: StepperSegmentProps) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Increase ${label.toLowerCase()}`}
        onClick={() => onStep(1)}
        className={ARROW_CLASS}
      >
        <ChevronUp className="size-4" />
      </Button>

      <Input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        aria-label={label}
        value={text}

        onChange={() => {}}
        onKeyDown={handlers.handleKeyDown}
        onFocus={handlers.handleFocus}
        onBlur={handlers.handleBlur}
        className={SEGMENT_CLASS}
      />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Decrease ${label.toLowerCase()}`}
        onClick={() => onStep(-1)}
        className={ARROW_CLASS}
      >
        <ChevronDown className="size-4" />
      </Button>
    </div>
  );
}

interface TimeStepperProps {
  hours: number;
  minutes: number;
  onChange: (hours: number, minutes: number) => void;

  minuteStep?: number;
  classNames?: DateTimePickerClassNames;
  autoFocus?: boolean;
}

export function TimeStepper({
  hours,
  minutes,
  onChange,
  minuteStep = 1,
  classNames,
}: TimeStepperProps) {
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);

  const hourSegment = useDigitSegment({
    min: 0,
    max: HOURS_PER_DAY - 1,
    value: hours,
    onCommit: (next) => onChange(next, minutes),
    onAdvance: () => minuteRef.current?.focus(),
  });

  const minuteSegment = useDigitSegment({
    min: 0,
    max: MINUTES_PER_HOUR - 1,
    value: minutes,
    onCommit: (next) => onChange(hours, next),
  });

  const step = Math.min(Math.max(1, Math.floor(minuteStep)), MINUTES_PER_HOUR);

  return (
    <div
      data-part="time-stepper"
      className={cn(
        "flex items-center justify-center gap-1 px-1 py-0.5",
        classNames?.timePanel,
      )}
    >
      <StepperSegment
        label="Hour"
        text={hourSegment.text}
        inputRef={hourRef}
        handlers={hourSegment}
        onStep={(delta) => onChange(wrap(hours + delta, HOURS_PER_DAY), minutes)}
      />

      <span aria-hidden className="pb-0.5 text-sm text-muted-foreground">
        :
      </span>

      <StepperSegment
        label="Minute"
        text={minuteSegment.text}
        inputRef={minuteRef}
        handlers={minuteSegment}

        onStep={(delta) => {
          const total = wrap(
            hours * MINUTES_PER_HOUR + minutes + delta * step,
            HOURS_PER_DAY * MINUTES_PER_HOUR,
          );
          onChange(Math.floor(total / MINUTES_PER_HOUR), total % MINUTES_PER_HOUR);
        }}
      />
    </div>
  );
}

export function TimeStepperPanel({
  field,
  minuteStep,
  classNames,
  autoFocus,
}: DateTimePickerPanelProps) {
  const [hours, minutes] = field.timeParts;

  return (
    <TimeStepper
      hours={hours}
      minutes={minutes}
      minuteStep={minuteStep}
      classNames={classNames}
      autoFocus={autoFocus}
      onChange={field.selectTime}
    />
  );
}
